import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ProjectForm from "./ProjectForm";
import ImageUploadArea from "./ImageUploadArea";
import ImageGallery from "./ImageGallery";
import { usePortfolioAPI } from "./usePortfolioAPI";
import { useFormValidation } from "./useFormValidation";

const PortfolioUpload = ({ open, onClose, onSuccess, editingProject }) => {
  const { userData, token } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    unitType: "",
    projectDate: null,
    images: [],
  });
  const [imageOrder, setImageOrder] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Custom hooks
  const { loading, uploading, uploadImages, saveProject, fetchProjectForEditing } = usePortfolioAPI();
  const { errors, clearError, validateForm } = useFormValidation();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (files) => {
    const uploadedImages = await uploadImages(files);
    if (uploadedImages.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      
      // Automatically assign order numbers based on upload sequence
      setImageOrder(prev => {
        const newOrder = { ...prev };
        const currentImageCount = formData.images.length;
        
        uploadedImages.forEach((image, index) => {
          const orderNumber = currentImageCount + index + 1;
          // Only assign order if it's 1, 2, or 3 (as per the current system)
          if (orderNumber <= 3) {
            newOrder[image.id] = orderNumber;
          }
        });
        
        return newOrder;
      });
    }
  };

  const handleImageOrderChange = (imageId, order) => {
    setImageOrder(prev => {
      const newOrder = { ...prev };
      
      // If clicking the same order, remove it
      if (newOrder[imageId] === order) {
        delete newOrder[imageId];
        return newOrder;
      }
      
      // If another image already has this order, remove it from that image
      Object.keys(newOrder).forEach(id => {
        if (newOrder[id] === order && id !== imageId) {
          delete newOrder[id];
        }
      });
      
      // Set the new order
      newOrder[imageId] = order;
      return newOrder;
    });
  };

  const handleRemoveImage = (index) => {
    const removedImage = formData.images[index];
    const removedOrder = imageOrder[removedImage?.id];
    
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    
    // Remove image order and reassign remaining orders
    setImageOrder(prev => {
      const newOrder = { ...prev };
      delete newOrder[removedImage?.id];
      
      // Reassign order numbers to maintain sequential order
      const remainingImages = formData.images.filter((_, i) => i !== index);
      const sortedImages = remainingImages
        .map((image, idx) => ({ image, originalIndex: idx }))
        .sort((a, b) => {
          const orderA = newOrder[a.image.id] || 999;
          const orderB = newOrder[b.image.id] || 999;
          return orderA - orderB;
        });
      
      // Reassign orders 1, 2, 3 to the first 3 images
      sortedImages.forEach(({ image }, idx) => {
        if (idx < 3) {
          newOrder[image.id] = idx + 1;
        } else {
          // Remove order for images beyond the first 3
          delete newOrder[image.id];
        }
      });
      
      return newOrder;
    });
  };

  const handleSubmit = async () => {
    if (!validateForm(formData)) {
      return;
    }

    try {
      const responseData = await saveProject(formData, imageOrder, null, editingProject, userData, token);
      onSuccess && onSuccess(responseData);
      handleClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Drag and drop handlers
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    handleImageUpload(files);
  };

  // Populate form when editingProject is provided
  useEffect(() => {
    const populateForm = async () => {
      if (editingProject) {
        console.log('useEffect triggered with editingProject:', editingProject);
        try {
          // Fetch the full project data with all images for editing
          const fullProjectData = await fetchProjectForEditing(editingProject.documentId || editingProject.id);
          
          // Handle new gallery structure
          let images = [];
          let imageOrder = {};
          
          if (fullProjectData.gallery && Array.isArray(fullProjectData.gallery)) {
            // New structure: gallery is array of objects with image and order
            images = fullProjectData.gallery
              .filter(item => item.image) // Only include items with images
              .map(item => item.image);
            
            // Build image order from gallery items (include all orders, including 0 for editing)
            fullProjectData.gallery.forEach(item => {
              if (item.image && item.order !== undefined) {
                imageOrder[item.image.id] = item.order;
              }
            });
          } else if (fullProjectData.image_gallery) {
            // Fallback to old structure
            images = fullProjectData.image_gallery;
            imageOrder = fullProjectData.image_order || {};
          }
          
          const formDataToSet = {
            title: fullProjectData.project_name || "",
            description: fullProjectData.project_description || "",
            location: fullProjectData.location || "",
            unitType: fullProjectData.unit_type || "",
            projectDate: fullProjectData.date || fullProjectData.project_date || fullProjectData.createdAt ? new Date(fullProjectData.date || fullProjectData.project_date || fullProjectData.createdAt) : null,
            images: images,
          };
          
          console.log('Setting form data from API:', formDataToSet);
          setFormData(formDataToSet);
          setImageOrder(imageOrder);
        } catch (error) {
          console.error('Error fetching project for editing:', error);
          // Fallback to using the limited data from the portfolio list
          const images = editingProject.gallery?.filter(item => item.image)?.map(item => item.image) || editingProject.image_gallery || [];
          const imageOrder = {};
          editingProject.gallery?.forEach(item => {
            if (item.image && item.order !== undefined) {
              imageOrder[item.image.id] = item.order;
            }
          });
          
          // Fallback to old structure if no gallery data
          if (Object.keys(imageOrder).length === 0) {
            Object.assign(imageOrder, editingProject.image_order || {});
          }
          
          const fallbackFormData = {
            title: editingProject.project_name || "",
            description: editingProject.project_description || "",
            location: editingProject.location || "",
            unitType: editingProject.unit_type || "",
            projectDate: editingProject.date || editingProject.project_date || editingProject.createdAt ? new Date(editingProject.date || editingProject.project_date || editingProject.createdAt) : null,
            images: images,
          };
          
          console.log('Setting form data from fallback:', fallbackFormData);
          setFormData(fallbackFormData);
          setImageOrder(imageOrder);
        }
      } else {
        // Reset form when creating new project
        console.log('Resetting form for new project');
        setFormData({
          title: "",
          description: "",
          location: "",
          unitType: "",
          projectDate: null,
          images: [],
        });
        setImageOrder({});
      }
    };

    populateForm();
  }, [editingProject]);

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      location: "",
      unitType: "",
      projectDate: null,
      images: [],
    });
    setImageOrder({});
    setIsDragOver(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {editingProject ? 'Edit Project' : 'Create Portfolio Album'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <ProjectForm
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
          onErrorClear={clearError}
          editingProject={editingProject}
        />

        {/* Image Upload Section */}
        <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#666' }}>
                Project Images
              </Typography>
              
          <ImageUploadArea
            onImageUpload={handleImageUpload}
            uploading={uploading}
            isDragOver={isDragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
          />

          <ImageGallery
            images={formData.images}
            imageOrder={imageOrder}
            onImageOrderChange={handleImageOrderChange}
            onRemoveImage={handleRemoveImage}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || formData.images.length === 0}
          sx={{
            textTransform: 'none',
            backgroundColor: '#4D6BF6',
            '&:hover': {
              backgroundColor: '#3B5BDB',
            }
          }}
        >
          {loading ? (editingProject ? 'Updating...' : 'Creating...') : (editingProject ? 'Update Project' : 'Create Project')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortfolioUpload;
